import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, getDocs, doc, updateDoc, getDoc, deleteDoc, setDoc,
  query, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, TextField,
  Avatar, Chip, Tooltip, Divider, Button, Dialog, Collapse,
  DialogTitle, DialogContent, DialogActions, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CakeIcon from '@mui/icons-material/Cake';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonIcon from '@mui/icons-material/Person';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { deptCode, deptLabel } from '../utils/depts';
import { calcProgress } from '../utils/onboardingTemplate';
import { canEditProfile, isUserAdmin } from '../utils/permissions';
import EmployeeImport from '../components/EmployeeImport';
import ImageUpload from '../components/ImageUpload';
import LagbasLogo from '../components/LagbasLogo';

const ROLE_LABELS = {
  superadmin: 'Superadmin', admin: 'Admin', gruppledare: 'Gruppledare',
  anbudsansvarig: 'Anbudsansvarig', medarbetare: 'Medarbetare',
};
const ROLE_OPTIONS = Object.entries(ROLE_LABELS);

const EMPTY_FORM = {
  displayName: '', kortnamn: '', title: '', email: '',
  mobile: '', officePhone: '', linkedin: '',
  startDate: '', endDate: '', birthDate: '',
  location: '', bio: '', photo: '',
  departmentId: '', role: 'medarbetare', employmentStatus: '',
  // Privata kontaktuppgifter (nödfall)
  homePhone: '', privateMobile: '', homeAddress: '', homeZip: '', homeCity: '', iceContact: '',
};

const INACTIVE_STATUSES = ['Slutat', 'Pensionär'];
const isInactive = (emp) => INACTIVE_STATUSES.includes(emp.employmentStatus);

// Bygger lista av rollbrickor för en medarbetare
function getRoleBadges(emp, depts) {
  const badges = [];
  const role = emp.role;

  // Systembehörighet
  if (role === 'superadmin') badges.push({ label: 'Superadmin', bg: '#ede7f6', color: '#4527a0' });
  else if (role === 'admin')   badges.push({ label: 'Admin',      bg: '#e3f2fd', color: '#1565c0' });
  else if (role === 'vd')      badges.push({ label: 'VD',         bg: '#e8f5e9', color: '#1b5e20' });

  // Avdelningsansvar (AO / GL)
  const isManager = ['ao_ansvarig', 'vd', 'superadmin', 'admin'].includes(role);
  const isGL      = role === 'gruppledare';
  const prefix    = isGL ? 'GL' : 'AO';

  const deptIds = [...new Set([emp.departmentId, ...(emp.managedDepartmentIds || [])].filter(Boolean))];
  deptIds.forEach(id => {
    const dept = depts.find(d => d.id === id);
    if (!dept) return;
    const code = dept.offerCode || dept.name;
    if (isManager || isGL) {
      badges.push({ label: `${prefix} · ${code}`, bg: '#fff3e0', color: '#e65100' });
    }
  });

  // Funktionsbeskrivning (från CSV) — GL/AA/VD-rader → orange chips
  if (emp.funktion) {
    emp.funktion.split('\n').filter(Boolean).forEach(f => {
      const m = f.match(/^([A-Z]+)\s*-\s*(.+)/);
      if (m) {
        const fPrefix = m[1]; // "GL", "AA", "VD" …
        const desc    = m[2].trim();
        const lastWord = desc.match(/(\S+)$/)?.[1] || desc; // last token = dept code
        if (['GL', 'AA', 'AO', 'VD', 'BT', 'BTM'].includes(fPrefix)) {
          badges.push({ label: `${fPrefix} · ${lastWord}`, bg: '#fff3e0', color: '#e65100' });
        } else {
          badges.push({ label: desc, bg: '#f5f5f5', color: '#424242' });
        }
      } else {
        const clean = f.trim();
        if (clean) badges.push({ label: clean, bg: '#f5f5f5', color: '#424242' });
      }
    });
  }

  return badges;
}

function formatBirthday(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  const months = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]} (${parts[0]})`;
}

export default function EmployeeDirectory() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [profilesMap, setProfilesMap] = useState({});
  const [iupDatesMap, setIupDatesMap] = useState({});
  const [generatingBio, setGeneratingBio] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ firstName: '', lastName: '', email: '', departmentId: '', startDate: '' });
  const [creating, setCreating] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  // Onboarding per employee-uid (lazy-loadad)
  const [empOnboarding, setEmpOnboarding] = useState({});
  // mentorMap: { mentorUid -> [{ id, employeeName, checks }] } – byggs vid load
  const [mentorMap, setMentorMap] = useState({});

  function toggleRow(id) {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Lazy-load onboarding för denna medarbetare om den inte redan är hämtad
        loadEmpOnboarding(id);
      }
      return next;
    });
  }

  async function loadEmpOnboarding(empId) {
    if (empOnboarding[empId] !== undefined) return;
    try {
      const obSnap = await getDocs(query(collection(db, 'onboarding'),
        where('employeeUid', '==', empId)));
      // Spara senaste onboarding (det kan bara finnas en aktiv)
      const ob = obSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))[0] || null;
      setEmpOnboarding(prev => ({ ...prev, [empId]: ob }));
    } catch {
      setEmpOnboarding(prev => ({ ...prev, [empId]: null }));
    }
  }

  function handleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    const [mySnap, empSnap, deptSnap, formerSnap, profilesSnap, pendingSnap, obSnap] = await Promise.all([
      getDoc(doc(db, 'users', currentUser.uid)),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
      getDocs(collection(db, 'formerEmployees')),
      getDocs(collection(db, 'profiles')),
      getDocs(collection(db, 'pendingEmployees')),
      getDocs(collection(db, 'onboarding')),
    ]);
    // Bygg mentorMap: { mentorUid -> [{ id, employeeName, checks }] }
    const mm = {};
    obSnap.docs.forEach(d => {
      const ob = d.data();
      if (ob.mentorUid) {
        if (!mm[ob.mentorUid]) mm[ob.mentorUid] = [];
        mm[ob.mentorUid].push({ id: d.id, employeeName: ob.employeeName || '', checks: ob.checks || {} });
      }
    });
    setMentorMap(mm);
    const pm = {};
    profilesSnap.docs.forEach(d => {
      const data = d.data();
      pm[d.id] = {
        ...(data?.personal || {}),
        _cvUpdatedAt: data?.updatedAt?.toDate?.() || null,
        photo: data?.personal?.photo || null,
      };
    });
    setProfilesMap(pm);
    // Senaste IUP per användare. IUP:erna själva får bara ägaren och chefer
    // läsa – datumet hålls i users.iupSenast av Cloud Function onIupWritten.
    const iupDates = {};
    empSnap.docs.forEach(d => {
      const date = d.data().iupSenast?.toDate?.();
      if (date) iupDates[d.id] = date;
    });
    setIupDatesMap(iupDates);
    const myRole = mySnap.data()?.role || '';
    setIsAdmin(['superadmin', 'admin', 'ao_ansvarig', 'vd', 'gruppledare'].includes(myRole));
    const activeUsers = empSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(u => u.email);
    const formerUsers = formerSnap.docs
      .map(d => ({ id: d.id, ...d.data(), _isFormerRecord: true }));
    const pendingUsers = pendingSnap.docs
      .map(d => ({ id: d.id, ...d.data(), _isPending: true }));
    setEmployees(
      [...activeUsers, ...formerUsers, ...pendingUsers]
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'sv'))
    );
    setDepartments(deptSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => d.name?.toLowerCase() !== 'admin')
    );
    setLoading(false);
  }, [currentUser.uid]);

  useEffect(() => { load(); }, [load]);

  function openEdit(emp) {
    const cv = profilesMap[emp.id] || {};
    setForm({
      displayName: emp.displayName || cv.name || '',
      kortnamn: emp.kortnamn || '',
      title: emp.title || cv.title || '',
      email: emp.email || cv.email || '',
      mobile: emp.mobile || emp.signatureMobile || cv.phone || '',
      officePhone: emp.officePhone || emp.signatureOffice || '',
      linkedin: emp.linkedin || emp.signaturePersonalLinkedIn || cv.linkedin || '',
      startDate: emp.startDate || '',
      endDate: emp.endDate || '',
      birthDate: emp.birthDate || '',
      location: emp.location || cv.location || '',
      bio: emp.bio || '',
      photo: cv.photo || emp.photoUrl || '',
      homePhone: emp.homePhone || '',
      privateMobile: emp.privateMobile || '',
      homeAddress: emp.homeAddress || '',
      homeZip: emp.homeZip || '',
      homeCity: emp.homeCity || '',
      iceContact: emp.iceContact || '',
      departmentId: emp.departmentId || '',
      role: emp.role || 'medarbetare',
      employmentStatus: emp.employmentStatus || '',
    });
    setEditTarget(emp);
  }

  const canEdit = (emp) => isAdmin || emp.id === currentUser.uid;
  const canAssignRoles = isUserAdmin(userProfile);

  async function handleSave() {
    if (!editTarget) return;
    setSaving(true);
    const updates = {
      displayName: form.displayName.trim(),
      kortnamn: form.kortnamn.trim(),
      title: form.title.trim(),
      mobile: form.mobile.trim(),
      officePhone: form.officePhone.trim(),
      linkedin: form.linkedin.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      birthDate: form.birthDate,
      location: form.location.trim(),
      bio: form.bio.trim(),
      employmentStatus: form.employmentStatus,
      homePhone: form.homePhone.trim(),
      privateMobile: form.privateMobile.trim(),
      homeAddress: form.homeAddress.trim(),
      homeZip: form.homeZip.trim(),
      homeCity: form.homeCity.trim(),
      iceContact: form.iceContact.trim(),
      // canonical sync for backward compat
      signatureMobile: form.mobile.trim(),
      signatureOffice: form.officePhone.trim(),
      signaturePersonalLinkedIn: form.linkedin.trim(),
    };
    if (isAdmin) {
      updates.departmentId = form.departmentId;
    }
    // Systemroll får bara admin ändra (låst i firestore.rules)
    if (canAssignRoles && form.role !== (editTarget.role || 'medarbetare')) {
      updates.role = form.role;
    }
    await updateDoc(doc(db, 'users', editTarget.id), updates);

    // Synka kanoniska fält tillbaka till profiles (om det inte är ett externt tidigare-konto)
    if (!editTarget._isFormerRecord) {
      const profileSync = {
        name: updates.displayName || '',
        title: updates.title || '',
        location: updates.location || '',
      };
      if (form.photo) profileSync.photo = form.photo;
      await setDoc(doc(db, 'profiles', editTarget.id), {
        personal: profileSync
      }, { merge: true });
      setProfilesMap(pm => ({
        ...pm,
        [editTarget.id]: {
          ...(pm[editTarget.id] || {}),
          name: updates.displayName || '',
          title: updates.title || '',
          location: updates.location || '',
          ...(form.photo ? { photo: form.photo } : {}),
        }
      }));
    }

    setEmployees(prev => prev.map(e =>
      e.id === editTarget.id ? { ...e, ...updates } : e
    ));
    setSaving(false);
    setEditTarget(null);
    setSaved('Sparad!');
    setTimeout(() => setSaved(''), 2500);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const coll = deleteTarget._isFormerRecord ? 'formerEmployees' : 'users';
    await deleteDoc(doc(db, coll, deleteTarget.id));
    setEmployees(prev => prev.filter(e => e.id !== deleteTarget.id));
    setDeleteTarget(null);
    setDeleting(false);
    setSaved('Medarbetare raderad (GDPR).');
    setTimeout(() => setSaved(''), 3000);
  }

  async function handleCreate() {
    if (!createForm.email && !createForm.firstName) return;
    setCreating(true);
    const displayName = [createForm.firstName.trim(), createForm.lastName.trim()].filter(Boolean).join(' ');
    const email = createForm.email.trim().toLowerCase();
    const key = email || displayName.toLowerCase().replace(/\s+/g, '.');
    await setDoc(doc(db, 'pendingEmployees', key), {
      displayName, email,
      departmentId: createForm.departmentId,
      role: 'medarbetare',
      startDate: createForm.startDate,
      createdAt: new Date(),
    });
    setCreating(false);
    setCreateOpen(false);
    setCreateForm({ firstName: '', lastName: '', email: '', departmentId: '', startDate: '' });
    setSaved(`${displayName || email} skapad — appliceras vid inloggning.`);
    setTimeout(() => setSaved(''), 4000);
  }

  const filtered = employees.filter(e => {
    if (filterDept && e.departmentId !== filterDept) return false;
    if (filterStatus === 'active' && (isInactive(e) || e._isFormerRecord)) return false;
    if (filterStatus === 'inactive' && !isInactive(e) && !e._isFormerRecord) return false;
    if (e._isPending && filterStatus === 'inactive') return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        e.displayName?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.title?.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    let av = '', bv = '';
    if (sortField === 'name') { av = a.displayName || a.email || ''; bv = b.displayName || b.email || ''; }
    else if (sortField === 'dept') { av = a.departmentId || ''; bv = b.departmentId || ''; }
    else if (sortField === 'title') { av = a.title || ''; bv = b.title || ''; }
    else if (sortField === 'location') { av = a.location || ''; bv = b.location || ''; }
    const cmp = av.localeCompare(bv, 'sv');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const deptOf = (id) => departments.find(d => d.id === id);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">Medarbetarregister</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Centrala uppgifter — matar offert, mailsignatur, CV och IUP
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {filtered.length} / {employees.filter(e => !isInactive(e) && !e._isFormerRecord && !e._isPending).length} aktiva
        </Typography>
        {isAdmin && (<>
          <Button size="small" variant="outlined"
            startIcon={<AddIcon />}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            onClick={() => setCreateOpen(true)}>
            Ny medarbetare
          </Button>
          <Button size="small" variant="outlined"
            startIcon={<UploadFileIcon />}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            onClick={() => setImportOpen(true)}>
            Importera
          </Button>
        </>)}
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        {/* Dashboard */}
        {isAdmin && (() => {
          const active = employees.filter(e => !isInactive(e) && !e._isFormerRecord && !e._isPending);
          const pending = employees.filter(e => e._isPending);
          const deptCounts = departments.map(d => ({
            dept: d,
            count: active.filter(e => e.departmentId === d.id).length,
          })).filter(x => x.count > 0).sort((a, b) => b.count - a.count);

          const today = new Date(); today.setHours(0,0,0,0);
          const withBday = active.filter(e => e.birthDate).map(e => {
            const [,m,d] = e.birthDate.split('-').map(Number);
            let next = new Date(today.getFullYear(), m - 1, d);
            if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
            const days = Math.round((next - today) / 86400000);
            return { emp: e, next, days };
          }).sort((a, b) => a.days - b.days).slice(0, 5);

          return (
            <Paper sx={{ mb: 3, p: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Totals + per-AO */}
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                  Bemanning
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#FF6B00', lineHeight: 1 }}>{active.length}</Typography>
                    <Typography variant="caption" color="text.secondary">Aktiva</Typography>
                  </Box>
                  {pending.length > 0 && (
                    <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#FF6B00', lineHeight: 1 }}>{pending.length}</Typography>
                      <Typography variant="caption" color="text.secondary">Ej inloggade</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {deptCounts.map(({ dept, count }) => (
                    <Chip key={dept.id}
                      label={`${deptCode(dept)} · ${count}`}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                  ))}
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

              {/* Upcoming birthdays */}
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                  Kommande födelsedagar
                </Typography>
                {withBday.length === 0
                  ? <Typography variant="caption" color="text.disabled">Inga registrerade</Typography>
                  : withBday.map(({ emp: e, next, days }) => (
                    <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                      <CakeIcon sx={{ fontSize: 14, color: days <= 7 ? '#e53935' : '#bdbdbd', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', flex: 1 }} noWrap>
                        {e.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}>
                        {next.toLocaleDateString('sv', { day: 'numeric', month: 'short' })}
                        {' '}
                        <Typography component="span" variant="caption"
                          sx={{ fontSize: '0.7rem', color: days <= 7 ? '#e53935' : 'text.disabled', fontWeight: days <= 7 ? 700 : 400 }}>
                          ({days === 0 ? 'idag!' : days === 1 ? 'imorgon' : `${days} dagar`})
                        </Typography>
                      </Typography>
                    </Box>
                  ))
                }
              </Box>
            </Paper>
          );
        })()}

        {/* Toolbar */}
        <Paper sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Sök namn, titel, ort..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
            sx={{ width: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Affärsområde</InputLabel>
            <Select value={filterDept} label="Affärsområde"
              onChange={e => setFilterDept(e.target.value)}>
              <MenuItem value="">Alla</MenuItem>
              {departments.map(d => (
                <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status"
              onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">Alla</MenuItem>
              <MenuItem value="active">Aktiva</MenuItem>
              <MenuItem value="inactive">Slutat / Pensionär</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Employee list */}
        {loading ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>Laddar...</Typography>
        ) : (
          <Paper sx={{ overflow: 'hidden' }}>
            {/* Column headers */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr 1fr 1.6fr 80px',
              px: 2, py: 1,
              bgcolor: '#f5f5f5',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {[
                { field: 'name', label: 'Namn' },
                { field: 'status', label: 'Status', noSort: true },
                { field: 'dept', label: 'Affärsområde' },
                { field: 'location', label: 'Ort' },
              ].map(col => (
                <Box key={col.field}
                  onClick={() => !col.noSort && handleSort(col.field)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                    cursor: col.noSort ? 'default' : 'pointer',
                    userSelect: 'none', '&:hover': col.noSort ? {} : { color: '#FF6B00' } }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem' }}>
                    {col.label}
                  </Typography>
                  {!col.noSort && sortField === col.field
                    ? sortDir === 'asc'
                      ? <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                      : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    : null}
                </Box>
              ))}
              <Box />
            </Box>

            {sortedFiltered.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                Inga medarbetare matchar sökningen.
              </Typography>
            )}

            {sortedFiltered.map((emp, idx) => {
              const dept = deptOf(emp.departmentId);
              const phone = emp.mobile || emp.signatureMobile || '';
              const inactive = isInactive(emp);
              const isPending = !!emp._isPending;
              const expanded = expandedRows.has(emp.id);
              const initials = (emp.displayName || emp.email || '?')
                .split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
              const isMe = emp.id === currentUser.uid;

              return (
                <Box key={emp.id} sx={{
                  borderBottom: idx < sortedFiltered.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  borderLeft: isMe ? '3px solid #FF6B00' : isPending ? '3px solid #bdbdbd' : inactive ? '3px solid #e0e0e0' : '3px solid transparent',
                  opacity: (inactive || isPending) && !expanded ? 0.65 : 1,
                  transition: 'background .1s',
                  bgcolor: expanded ? 'rgba(255,107,0,0.03)' : 'transparent',
                }}>
                  {/* Collapsed row */}
                  <Box
                    onClick={() => toggleRow(emp.id)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1fr 1fr 1.6fr 80px',
                      px: 2, py: 1.1,
                      cursor: 'pointer',
                      alignItems: 'center',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}>
                    {/* Name column */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Avatar
                        src={profilesMap[emp.id]?.photo || emp.photoUrl || undefined}
                        sx={{
                          width: 32, height: 32, fontSize: 12, fontWeight: 700, flexShrink: 0,
                          bgcolor: inactive ? '#bdbdbd' : isPending ? '#9e9e9e' : isMe ? '#FF6B00' : '#424242',
                          outline: isMe ? '2px solid #FF6B00' : 'none',
                        }}>
                        {initials}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isPending ? 'text.disabled' : 'inherit' }}>
                          {emp.displayName || emp.email}
                          {emp.kortnamn && (
                            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                              ({emp.kortnamn})
                            </Typography>
                          )}
                        </Typography>
                        {isPending ? (
                          <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#9e9e9e', fontStyle: 'italic' }}>
                            Ej loggat in ännu
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                            {emp.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                      {isAdmin && !emp.iceContact && !inactive && !isPending && !emp._isFormerRecord && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WarningAmberIcon sx={{ fontSize: 18, color: '#e53935', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#e53935', fontWeight: 700 }}>
                            ICE saknas
                          </Typography>
                        </Box>
                      )}
                      {isPending && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WarningAmberIcon sx={{ fontSize: 18, color: '#FF6B00', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#FF6B00', fontWeight: 700 }}>
                            Ej inloggad
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Dept */}
                    <Box>
                      {dept ? (
                        <Tooltip title={dept.name}>
                          <Chip label={deptCode(dept)} size="small"
                            sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fff3e0', color: '#e65100' }} />
                        </Tooltip>
                      ) : (inactive || emp._isFormerRecord) ? (
                        <Chip label={emp.employmentStatus || 'Fd. medarbetare'} size="small"
                          sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#f5f5f5', color: '#757575' }} />
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </Box>

                    {/* Location */}
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.82rem' }}>
                      {emp.location || '—'}
                    </Typography>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
                      onClick={e => e.stopPropagation()}>
                      {canEdit(emp) && (
                        <Tooltip title="Redigera">
                          <IconButton size="small" onClick={() => openEdit(emp)}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdmin && (inactive || emp._isFormerRecord) && (
                        <Tooltip title="Radera (GDPR)">
                          <IconButton size="small" onClick={() => setDeleteTarget(emp)} sx={{ color: 'error.light' }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" onClick={() => toggleRow(emp.id)}>
                        {expanded
                          ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                          : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Expanded detail */}
                  <Collapse in={expanded}>
                    <Box sx={{
                      px: 3, pb: 2, pt: 0.5,
                      display: 'flex', gap: 4, flexWrap: 'wrap',
                      bgcolor: 'rgba(255,107,0,0.03)',
                      borderTop: '1px dashed', borderColor: 'divider',
                    }}>
                      {/* Contact block */}
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 0.75, mt: 1.25 }}>
                          Kontakt
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <ContactRow icon={<EmailIcon sx={{ fontSize: 13 }} />} href={`mailto:${emp.email}`} label={emp.email} />
                          {phone && <ContactRow icon={<PhoneIcon sx={{ fontSize: 13 }} />} href={`tel:${phone}`} label={phone} />}
                          {emp.officePhone && emp.officePhone !== phone && (
                            <ContactRow icon={<BusinessIcon sx={{ fontSize: 13 }} />} href={`tel:${emp.officePhone}`} label={emp.officePhone} />
                          )}
                          {(emp.linkedin || emp.signaturePersonalLinkedIn) && (
                            <ContactRow icon={<LinkedInIcon sx={{ fontSize: 13 }} />}
                              href={emp.linkedin || emp.signaturePersonalLinkedIn} label="LinkedIn" external />
                          )}
                        </Box>
                      </Box>

                      {/* Dates block */}
                      <Box sx={{ minWidth: 180 }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 0.75, mt: 1.25 }}>
                          Anställning
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {emp.startDate && <ContactRow icon={<CalendarTodayIcon sx={{ fontSize: 13 }} />} label={`Anst. ${emp.startDate}`} />}
                          {emp.endDate && <ContactRow icon={<CalendarTodayIcon sx={{ fontSize: 13 }} />} label={`Slutat ${emp.endDate}`} />}
                          {emp.birthDate && <ContactRow icon={<CakeIcon sx={{ fontSize: 13 }} />} label={formatBirthday(emp.birthDate)} />}
                          {getRoleBadges(emp, departments).length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {getRoleBadges(emp, departments).map((b, i) => (
                                <Chip key={i} label={b.label} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20, bgcolor: b.bg, color: b.color, fontWeight: 500 }} />
                              ))}
                            </Box>
                          )}
                          {mentorMap[emp.id]?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {mentorMap[emp.id].map(ob => {
                                const pct = calcProgress(ob.checks);
                                return (
                                  <Tooltip key={ob.id} title={`Gå till onboarding (${pct}% klar)`}>
                                    <Chip
                                      size="small"
                                      label={`Mentor för ${ob.employeeName || '…'}`}
                                      onClick={() => navigate(`/onboarding/${ob.id}`)}
                                      sx={{
                                        fontSize: '0.65rem', height: 20, cursor: 'pointer',
                                        bgcolor: pct === 100 ? '#E8F5E9' : '#f3e5f5',
                                        color: pct === 100 ? '#2E7D32' : '#6a1b9a',
                                        fontWeight: 600,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Bio */}
                      {emp.bio && (
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 0.75, mt: 1.25 }}>
                            Presentation
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                            {emp.bio}
                          </Typography>
                        </Box>
                      )}

                      {/* Snabblänkar */}
                      {!emp._isFormerRecord && (
                        <Box sx={{ minWidth: 160 }}>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem', display: 'block', mb: 0.75, mt: 1.25 }}>
                            Snabblänkar
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {/* 1. Konsultprofil (medarbetarregister) */}
                            <Button size="small" variant="outlined" startIcon={<PersonIcon sx={{ fontSize: 14 }} />}
                              onClick={() => navigate(
                                emp.id === currentUser.uid ? '/profile'
                                  : (canEditProfile(userProfile, currentUser.uid, emp.id) && !emp._isPending) ? `/profile/${emp.id}`
                                  : `/cv-preview/${emp.id}`)}
                              sx={{ justifyContent: 'flex-start', fontSize: '0.72rem', py: 0.3, borderColor: 'divider', color: 'text.primary' }}>
                              {emp.id === currentUser.uid || (canEditProfile(userProfile, currentUser.uid, emp.id) && !emp._isPending) ? 'Redigera profil' : 'Konsultprofil'}
                              {profilesMap[emp.id]?._cvUpdatedAt && (
                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                  {profilesMap[emp.id]._cvUpdatedAt.toLocaleDateString('sv')}
                                </Typography>
                              )}
                            </Button>
                            {/* 3. Onboarding – dölj om 100% klar */}
                            {isAdmin && (() => {
                              const ob = empOnboarding[emp.id];
                              const pct = ob ? calcProgress(ob.checks) : null;
                              if (ob && pct < 100) {
                                const barColor = pct >= 50 ? '#FF6B00' : '#1565C0';
                                return (
                                  <Button size="small" variant="outlined"
                                    startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 14, color: barColor }} />}
                                    onClick={() => navigate(`/onboarding/${ob.id}`)}
                                    sx={{ justifyContent: 'flex-start', fontSize: '0.72rem', py: 0.3,
                                      borderColor: barColor, color: barColor }}>
                                    Onboarding {pct}%
                                  </Button>
                                );
                              }
                              if (!ob) {
                                return (
                                  <Button size="small" variant="outlined"
                                    startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => navigate('/onboarding')}
                                    sx={{ justifyContent: 'flex-start', fontSize: '0.72rem', py: 0.3,
                                      borderColor: 'divider', color: 'text.secondary' }}>
                                    Starta onboarding
                                  </Button>
                                );
                              }
                              return null;
                            })()}
                            {/* 4. IUP */}
                            <Button size="small" variant="outlined" startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                              onClick={() => navigate('/hr/iup')}
                              sx={{ justifyContent: 'flex-start', fontSize: '0.72rem', py: 0.3, borderColor: 'divider', color: 'text.primary' }}>
                              IUP
                              {iupDatesMap[emp.id] && (
                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                  {iupDatesMap[emp.id].toLocaleDateString('sv')}
                                </Typography>
                              )}
                            </Button>
                            {/* Mailsignatur (bara för sig själv) */}
                            {emp.id === currentUser.uid && (
                              <Button size="small" variant="outlined" startIcon={<ArticleIcon sx={{ fontSize: 14 }} />}
                                onClick={() => navigate('/marketing/signature')}
                                sx={{ justifyContent: 'flex-start', fontSize: '0.72rem', py: 0.3, borderColor: 'divider', color: 'text.primary' }}>
                                Mailsignatur
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Privata kontaktuppgifter – bara synliga för admin */}
                      {isAdmin && (emp.homePhone || emp.privateMobile || emp.homeAddress || emp.homeCity || emp.iceContact) && (
                        <Box sx={{ minWidth: 220, borderLeft: '2px solid #ef9a9a', pl: 1.5 }}>
                          <Typography variant="caption" fontWeight="bold"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.65rem',
                              display: 'block', mb: 0.75, mt: 1.25, color: '#c62828' }}>
                            Privat kontakt (nödfall)
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {emp.homePhone && <ContactRow icon={<PhoneIcon sx={{ fontSize: 13 }} />} href={`tel:${emp.homePhone}`} label={`Hem: ${emp.homePhone}`} />}
                            {emp.privateMobile && <ContactRow icon={<PhoneIcon sx={{ fontSize: 13 }} />} href={`tel:${emp.privateMobile}`} label={`Privat: ${emp.privateMobile}`} />}
                            {emp.homeAddress && <ContactRow icon={<BusinessIcon sx={{ fontSize: 13 }} />} label={emp.homeAddress} />}
                            {(emp.homeZip || emp.homeCity) && (
                              <ContactRow icon={<BusinessIcon sx={{ fontSize: 13 }} />}
                                label={[emp.homeZip, emp.homeCity].filter(Boolean).join(' ')} />
                            )}
                            {emp.iceContact && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="error.light" sx={{ fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                                  ICE: {emp.iceContact}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Paper>
        )}
      </Container>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
          {editTarget?.id === currentUser.uid ? 'Mina uppgifter' : `Redigera – ${editTarget?.displayName || editTarget?.email}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Identitet
            </Typography>
            <ImageUpload
              currentUrl={form.photo}
              path={`profiles/${editTarget?.id}`}
              onUpload={url => setForm(f => ({ ...f, photo: url }))}
              label="Ladda upp profilbild"
              variant="avatar"
            />
            <TextField label="Namn" value={form.displayName} fullWidth size="small"
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
            <TextField label="Kortnamn / smeknamn" value={form.kortnamn} fullWidth size="small"
              placeholder="t.ex. JoSt"
              onChange={e => setForm(f => ({ ...f, kortnamn: e.target.value }))} />
            <TextField label="Befattning / titel" value={form.title} fullWidth size="small"
              placeholder="t.ex. Konstruktionsingenjör"
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />

            <Divider />
            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Kontakt
            </Typography>
            <TextField label="E-post" value={form.email} fullWidth size="small"
              disabled helperText="E-postadressen hanteras via autentisering" />
            <TextField label="Mobiltelefon" value={form.mobile} fullWidth size="small"
              placeholder="+46 70 000 00 00"
              onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
              helperText="Används i offert och mailsignatur" />
            <TextField label="Kontorstelefon (valfritt)" value={form.officePhone} fullWidth size="small"
              onChange={e => setForm(f => ({ ...f, officePhone: e.target.value }))} />
            <TextField label="LinkedIn-URL (valfritt)" value={form.linkedin} fullWidth size="small"
              placeholder="https://linkedin.com/in/..."
              onChange={e => setForm(f => ({ ...f, linkedin: e.target.value }))} />

            <Divider />
            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Anställning & plats
            </Typography>
            <TextField label="Anställningsdatum" value={form.startDate} type="date"
              fullWidth size="small" InputLabelProps={{ shrink: true }}
              onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            <TextField label="Slutdatum" value={form.endDate} type="date"
              fullWidth size="small" InputLabelProps={{ shrink: true }}
              onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            <TextField label="Födelsedatum" value={form.birthDate} type="date"
              fullWidth size="small" InputLabelProps={{ shrink: true }}
              onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))} />
            <TextField label="Ort / kontor" value={form.location} fullWidth size="small"
              placeholder="t.ex. Stockholm"
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />

            {isAdmin && (
              <>
                <Divider />
                <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#c62828' }}>
                  Privat kontakt (nödfall) — synligt bara för admin
                </Typography>
                <TextField label="Telefon, hem" value={form.homePhone} fullWidth size="small"
                  onChange={e => setForm(f => ({ ...f, homePhone: e.target.value }))} />
                <TextField label="Mobiltelefon, privat" value={form.privateMobile} fullWidth size="small"
                  onChange={e => setForm(f => ({ ...f, privateMobile: e.target.value }))} />
                <TextField label="Hemadress" value={form.homeAddress} fullWidth size="small"
                  onChange={e => setForm(f => ({ ...f, homeAddress: e.target.value }))} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField label="Postnummer" value={form.homeZip} size="small" sx={{ width: 130 }}
                    onChange={e => setForm(f => ({ ...f, homeZip: e.target.value }))} />
                  <TextField label="Stad" value={form.homeCity} fullWidth size="small"
                    onChange={e => setForm(f => ({ ...f, homeCity: e.target.value }))} />
                </Box>
                <TextField label="Nära anhörig / ICE" value={form.iceContact} fullWidth size="small"
                  placeholder="Namn, telefon, relation"
                  onChange={e => setForm(f => ({ ...f, iceContact: e.target.value }))} />
                <Divider />
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Systeminställningar (admin)
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Affärsområde</InputLabel>
                  <Select value={form.departmentId} label="Affärsområde"
                    onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))}>
                    <MenuItem value="">Inget</MenuItem>
                    {departments.map(d => (
                      <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {canAssignRoles && (
                <FormControl fullWidth size="small">
                  <InputLabel>Systemroll</InputLabel>
                  <Select value={form.role} label="Systemroll"
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLE_OPTIONS
                      .filter(([val]) => val !== 'superadmin' || userProfile?.role === 'superadmin')
                      .map(([val, label]) => (
                        <MenuItem key={val} value={val}>{label}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel>Anställningsstatus</InputLabel>
                  <Select value={form.employmentStatus} label="Anställningsstatus"
                    onChange={e => setForm(f => ({ ...f, employmentStatus: e.target.value }))}>
                    <MenuItem value="">Okänd</MenuItem>
                    <MenuItem value="Aktiv medarbetare">Aktiv medarbetare</MenuItem>
                    <MenuItem value="Timanställd">Timanställd</MenuItem>
                    <MenuItem value="Slutat">Slutat</MenuItem>
                    <MenuItem value="Pensionär">Pensionär</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <Divider />
            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Presentation
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextField label="Kort presentation" value={form.bio} fullWidth size="small"
                multiline rows={3}
                placeholder="Kort om kompetens, bakgrund eller specialitet..."
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
              {editTarget && !editTarget._isFormerRecord && (
                <Tooltip title="Generera sammanfattning från CV med AI">
                  <span style={{ position: 'absolute', top: 8, right: 8 }}>
                    <Button
                      size="small" variant="outlined"
                      startIcon={generatingBio
                        ? <CircularProgress size={12} />
                        : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
                      disabled={generatingBio}
                      onClick={async () => {
                        setGeneratingBio(true);
                        try {
                          const profileSnap = await getDoc(doc(db, 'profiles', editTarget.id));
                          const p = profileSnap.exists() ? profileSnap.data() : {};
                          const parts = [];
                          if (p.personal?.title) parts.push(`Titel: ${p.personal.title}`);
                          if (p.personal?.location) parts.push(`Ort: ${p.personal.location}`);
                          if (p.competencies?.length) parts.push(`Kompetenser: ${p.competencies.slice(0, 8).join(', ')}`);
                          if (p.employment?.length) parts.push(`Senaste roller: ${p.employment.slice(0, 3).map(e => e.title + ' på ' + e.company).join('; ')}`);
                          if (p.education?.length) parts.push(`Utbildning: ${p.education.slice(0, 2).map(e => e.degree + ', ' + e.school).join('; ')}`);
                          if (!parts.length) { setGeneratingBio(false); return; }
                          const data = await callClaude({
                            model: 'claude-opus-5',
                            max_tokens: 200,
                            messages: [{
                              role: 'user',
                              content: `Skriv en kort professionell presentation (2 meningar, max 40 ord) om ${form.displayName || 'denna person'} baserat på: ${parts.join('. ')}. Skriv på svenska, i tredje person, utan att börja med namnet.`
                            }]
                          });
                          const bio = data.content?.[0]?.text?.trim() || '';
                          if (bio) setForm(f => ({ ...f, bio }));
                        } finally {
                          setGeneratingBio(false);
                        }
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.4, px: 1, minWidth: 0,
                        borderColor: '#FF6B00', color: '#FF6B00',
                        '&:hover': { borderColor: '#e65c00', bgcolor: 'rgba(255,107,0,0.06)' } }}>
                      {generatingBio ? 'Genererar...' : 'AI-bio'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, display: 'block' }}>
              Namn, titel och ort synkas automatiskt med CV-modulen vid sparning.
            </Typography>

            <Alert severity="info" icon={false} sx={{ fontSize: '0.75rem' }}>
              Mobiltelefon, LinkedIn och kontorstelefon används automatiskt i offert och mailsignatur.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar...' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GDPR delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon fontSize="small" /> Radera medarbetare (GDPR)
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Du är på väg att permanent radera alla uppgifter om{' '}
            <strong>{deleteTarget?.displayName || deleteTarget?.email}</strong> från LagbasN.
          </Typography>
          <Alert severity="warning" icon={false} sx={{ fontSize: '0.8rem' }}>
            Åtgärden kan inte ångras. Personens inloggningskonto berörs inte — enbart
            profil- och kontaktuppgifter i LagbasN raderas.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button variant="contained" color="error" disabled={deleting}
            onClick={handleDelete}>
            {deleting ? 'Raderar...' : 'Radera permanent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skapa ny medarbetare */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon sx={{ color: '#FF6B00' }} /> Ny medarbetare
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Förnamn" value={createForm.firstName} fullWidth size="small"
                onChange={e => setCreateForm(f => ({ ...f, firstName: e.target.value }))} />
              <TextField label="Efternamn" value={createForm.lastName} fullWidth size="small"
                onChange={e => setCreateForm(f => ({ ...f, lastName: e.target.value }))} />
            </Box>
            <TextField label="E-post (tikab.com)" value={createForm.email} fullWidth size="small"
              placeholder="fornamn.efternamn@tikab.com"
              helperText="Används vid inloggning — uppgifterna appliceras automatiskt"
              onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
            <FormControl fullWidth size="small">
              <InputLabel>Affärsområde</InputLabel>
              <Select value={createForm.departmentId} label="Affärsområde"
                onChange={e => setCreateForm(f => ({ ...f, departmentId: e.target.value }))}>
                <MenuItem value="">Inget</MenuItem>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Startdatum" value={createForm.startDate} type="date" fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              onChange={e => setCreateForm(f => ({ ...f, startDate: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Avbryt</Button>
          <Button variant="contained" disabled={creating || (!createForm.email && !createForm.firstName)}
            onClick={handleCreate}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}>
            {creating ? 'Skapar...' : 'Skapa'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmployeeImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { setImportOpen(false); load(); setSaved('Import klar — register uppdaterat!'); setTimeout(() => setSaved(''), 3000); }}
      />
    </Box>
  );
}

function ContactRow({ icon, href, label, external }) {
  const inner = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      <Box sx={{ color: 'text.disabled', flexShrink: 0, display: 'flex' }}>{icon}</Box>
      <Typography variant="caption" noWrap sx={{
        color: href ? 'primary.main' : 'text.secondary',
        textDecoration: href ? 'none' : 'none',
        '&:hover': href ? { textDecoration: 'underline' } : {},
      }}>
        {label}
      </Typography>
    </Box>
  );
  if (!href) return inner;
  return (
    <Box component="a" href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      sx={{ textDecoration: 'none' }}>
      {inner}
    </Box>
  );
}
